import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { createPortal } from "react-dom";
import { useDispatch, useSelector, shallowEqual } from "react-redux";
import { LOAD_EMPLOYEES, SG_GET_EMPLOYEE_ORG_CATEGORIES } from "constants/actions";

const DEFAULT_METRICS = ["count", "completed", "response_rate"];

const METRIC_DEFINITIONS = {
  count: { id: "count", label: "Count" },
  completed: { id: "completed", label: "Completed" },
  response_rate: { id: "response_rate", label: "Response Rate (%)" },
};

const textEncoder = typeof TextEncoder !== "undefined" ? new TextEncoder() : null;

const encodeString = (value) => {
  if (!textEncoder) {
    throw new Error("TextEncoder is not available in this environment.");
  }
  return textEncoder.encode(value);
};

const escapeXml = (value) =>
  String(value)
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;")
    .replace(/'/g, "&apos;");

const sanitizeSheetNameBase = (name) => {
  const cleaned = (name || "")
    .replace(/\|/g, " + ")
    .replace(/[\[\]\*\/\\\?\:]/g, " ")
    .replace(/\s+/g, " ")
    .trim();

  if (!cleaned) return "Sheet";
  return cleaned.slice(0, 31);
};

const makeUniqueSheetName = (baseName, existing) => {
  let name = sanitizeSheetNameBase(baseName);
  if (!existing.has(name)) {
    existing.add(name);
    return name;
  }
  let counter = 2;
  while (true) {
    const suffix = ` ${counter}`;
    const trimmed =
      name.length + suffix.length > 31 ? name.slice(0, 31 - suffix.length) : name;
    const candidate = `${trimmed}${suffix}`;
    if (!existing.has(candidate)) {
      existing.add(candidate);
      return candidate;
    }
    counter += 1;
  }
};

const getColumnLetter = (index) => {
  let col = index;
  let letter = "";
  while (col > 0) {
    const remainder = (col - 1) % 26;
    letter = String.fromCharCode(65 + remainder) + letter;
    col = Math.floor((col - 1) / 26);
  }
  return letter || "A";
};

const crc32Table = new Uint32Array(256).map((_, index) => {
  let c = index;
  for (let k = 0; k < 8; k += 1) {
    c = c & 1 ? 0xedb88320 ^ (c >>> 1) : c >>> 1;
  }
  return c >>> 0;
});

const crc32 = (bytes) => {
  let crc = 0xffffffff;
  for (let i = 0; i < bytes.length; i += 1) {
    const byte = bytes[i];
    crc = (crc >>> 8) ^ crc32Table[(crc ^ byte) & 0xff];
  }
  return (crc ^ 0xffffffff) >>> 0;
};

const concatUint8Arrays = (arrays) => {
  const length = arrays.reduce((acc, arr) => acc + arr.length, 0);
  const result = new Uint8Array(length);
  let offset = 0;
  arrays.forEach((arr) => {
    result.set(arr, offset);
    offset += arr.length;
  });
  return result;
};

const getDosDateTime = (date) => {
  const year = date.getFullYear();
  const month = date.getMonth() + 1;
  const day = date.getDate();
  const hours = date.getHours();
  const minutes = date.getMinutes();
  const seconds = Math.floor(date.getSeconds() / 2);

  const dosDate =
    ((year - 1980) << 9) | (month << 5) | day;
  const dosTime =
    (hours << 11) | (minutes << 5) | seconds;

  return { dosDate, dosTime };
};

const createZip = (files) => {
  const fileSegments = [];
  const centralDirectorySegments = [];
  let offset = 0;
  const now = new Date();
  const { dosDate, dosTime } = getDosDateTime(now);

  files.forEach((file) => {
    const filenameBytes = encodeString(file.name);
    const dataBytes = file.data instanceof Uint8Array ? file.data : encodeString(file.data);
    const crc = crc32(dataBytes);

    const localHeader = new Uint8Array(30);
    const localView = new DataView(localHeader.buffer);
    localView.setUint32(0, 0x04034b50, true); // signature
    localView.setUint16(4, 20, true); // version needed
    localView.setUint16(6, 0, true); // flags
    localView.setUint16(8, 0, true); // compression (0 = store)
    localView.setUint16(10, dosTime, true); // mod time
    localView.setUint16(12, dosDate, true); // mod date
    localView.setUint32(14, crc, true);
    localView.setUint32(18, dataBytes.length, true);
    localView.setUint32(22, dataBytes.length, true);
    localView.setUint16(26, filenameBytes.length, true);
    localView.setUint16(28, 0, true); // extra length

    const localRecord = concatUint8Arrays([localHeader, filenameBytes, dataBytes]);
    const localOffset = offset;
    offset += localRecord.length;
    fileSegments.push(localRecord);

    const centralHeader = new Uint8Array(46);
    const centralView = new DataView(centralHeader.buffer);
    centralView.setUint32(0, 0x02014b50, true); // signature
    centralView.setUint16(4, 20, true); // version made by
    centralView.setUint16(6, 20, true); // version needed
    centralView.setUint16(8, 0, true); // flags
    centralView.setUint16(10, 0, true); // compression
    centralView.setUint16(12, dosTime, true);
    centralView.setUint16(14, dosDate, true);
    centralView.setUint32(16, crc, true);
    centralView.setUint32(20, dataBytes.length, true);
    centralView.setUint32(24, dataBytes.length, true);
    centralView.setUint16(28, filenameBytes.length, true);
    centralView.setUint16(30, 0, true); // extra length
    centralView.setUint16(32, 0, true); // comment length
    centralView.setUint16(34, 0, true); // disk number
    centralView.setUint16(36, 0, true); // internal attrs
    centralView.setUint32(38, 0, true); // external attrs
    centralView.setUint32(42, localOffset, true);

    const centralRecord = concatUint8Arrays([centralHeader, filenameBytes]);
    centralDirectorySegments.push(centralRecord);
  });

  const centralDirectory = concatUint8Arrays(centralDirectorySegments);
  const centralOffset = offset;
  offset += centralDirectory.length;

  const endRecord = new Uint8Array(22);
  const endView = new DataView(endRecord.buffer);
  endView.setUint32(0, 0x06054b50, true); // signature
  endView.setUint16(4, 0, true); // disk number
  endView.setUint16(6, 0, true); // start disk
  endView.setUint16(8, files.length, true);
  endView.setUint16(10, files.length, true);
  endView.setUint32(12, centralDirectory.length, true);
  endView.setUint32(16, centralOffset, true);
  endView.setUint16(20, 0, true); // comment length

  return concatUint8Arrays([...fileSegments, centralDirectory, endRecord]);
};

const buildWorksheetXml = ({ heading, headers, rows, rowMetadata = [] }) => {
  const rowXml = [];
  let currentRow = 1;

  const totalColumns = headers.length;
  const lastColumn = getColumnLetter(totalColumns === 0 ? 1 : totalColumns);

  if (heading) {
    rowXml.push(
      `<row r="${currentRow}"><c r="A${currentRow}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(
        heading
      )}</t></is></c></row>`
    );
    currentRow += 1;
  }

  if (headers.length) {
    const headerCells = headers
      .map((header, columnIndex) => {
        const colLetter = getColumnLetter(columnIndex + 1);
        return `<c r="${colLetter}${currentRow}" t="inlineStr"><is><t xml:space="preserve">${escapeXml(
          header
        )}</t></is></c>`;
      })
      .join("");
    rowXml.push(`<row r="${currentRow}">${headerCells}</row>`);
    currentRow += 1;
  }

  rows.forEach((row, rowIndex) => {
    const metadata = rowMetadata[rowIndex] || {};
    const isTotalRow = metadata.isTotalRow || false;
    const cellStyleId = isTotalRow ? 1 : 0; // Style 0 = normal, Style 1 = red background

    const cells = row.map((value, columnIndex) => {
      const colLetter = getColumnLetter(columnIndex + 1);
      const cellRef = `${colLetter}${currentRow}`;

      // Determine style: 
      // 0 = default, 1 = red background (total row), 2 = right-aligned, 3 = red background + right-aligned
      let styleId = cellStyleId > 0 ? cellStyleId : 0;
      // Right-align asterisks and response rate values (strings ending with %)
      const shouldRightAlign = value === "*" || (typeof value === "string" && value.endsWith("%"));
      if (shouldRightAlign) {
        if (styleId === 1) {
          styleId = 3; // Red background + right-aligned for total rows
        } else if (styleId === 0) {
          styleId = 2; // Right-aligned for normal rows
        }
      }
      const styleAttr = styleId > 0 ? ` s="${styleId}"` : "";

      if (typeof value === "number" && Number.isFinite(value)) {
        return `<c r="${cellRef}"${styleAttr}><v>${value}</v></c>`;
      }
      if (value === null || value === undefined || value === "") {
        return `<c r="${cellRef}"${styleAttr} />`;
      }
      return `<c r="${cellRef}" t="inlineStr"${styleAttr}><is><t xml:space="preserve">${escapeXml(
        value
      )}</t></is></c>`;
    });
    rowXml.push(`<row r="${currentRow}">${cells.join("")}</row>`);
    currentRow += 1;
  });

  const lastRow = Math.max(currentRow - 1, 1);
  const dimension = `A1:${lastColumn}${lastRow}`;

  return `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<worksheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    `<dimension ref="${dimension}" />` +
    `<sheetViews><sheetView workbookViewId="0"/></sheetViews>` +
    `<sheetFormatPr defaultRowHeight="15"/>` +
    `<sheetData>${rowXml.join("")}</sheetData>` +
    `</worksheet>`;
};

const buildWorkbookZip = ({ sheets, workbookTitle }) => {
  const now = new Date();
  const isoNow = now.toISOString();
  const usedNames = new Set();

  const worksheetFiles = sheets.map((sheet, index) => {
    const sheetName = makeUniqueSheetName(sheet.name || `Sheet ${index + 1}`, usedNames);
    const worksheetXml = buildWorksheetXml({
      heading: sheet.heading,
      headers: sheet.headers,
      rows: sheet.rows,
      rowMetadata: sheet.rowMetadata || [],
    });
    return {
      name: `xl/worksheets/sheet${index + 1}.xml`,
      data: encodeString(worksheetXml),
      sheetName,
    };
  });

  const sheetEntries = worksheetFiles
    .map(
      (sheet, index) =>
        `<sheet name="${escapeXml(sheet.sheetName)}" sheetId="${index + 1}" r:id="rId${index + 1}"/>`
    )
    .join("");

  const contentTypes =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Types xmlns="http://schemas.openxmlformats.org/package/2006/content-types">` +
    `<Default Extension="rels" ContentType="application/vnd.openxmlformats-package.relationships+xml"/>` +
    `<Default Extension="xml" ContentType="application/xml"/>` +
    `<Override PartName="/xl/workbook.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet.main+xml"/>` +
    `<Override PartName="/xl/styles.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.styles+xml"/>` +
    `<Override PartName="/docProps/core.xml" ContentType="application/vnd.openxmlformats-package.core-properties+xml"/>` +
    `<Override PartName="/docProps/app.xml" ContentType="application/vnd.openxmlformats-officedocument.extended-properties+xml"/>` +
    worksheetFiles
      .map(
        (_, index) =>
          `<Override PartName="/xl/worksheets/sheet${index + 1}.xml" ContentType="application/vnd.openxmlformats-officedocument.spreadsheetml.worksheet+xml"/>`
      )
      .join("") +
    `</Types>`;

  const rootRels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    `<Relationship Id="rId1" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/officeDocument" Target="xl/workbook.xml"/>` +
    `<Relationship Id="rId2" Type="http://schemas.openxmlformats.org/package/2006/relationships/metadata/core-properties" Target="docProps/core.xml"/>` +
    `<Relationship Id="rId3" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/extended-properties" Target="docProps/app.xml"/>` +
    `</Relationships>`;

  const workbookXml =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<workbook xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main" ` +
    `xmlns:r="http://schemas.openxmlformats.org/officeDocument/2006/relationships">` +
    `<sheets>${sheetEntries}</sheets></workbook>`;

  const workbookRelsEntries = worksheetFiles
    .map(
      (_, index) =>
        `<Relationship Id="rId${index + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/worksheet" Target="worksheets/sheet${index + 1}.xml"/>`
    )
    .join("") +
    `<Relationship Id="rId${worksheetFiles.length + 1}" Type="http://schemas.openxmlformats.org/officeDocument/2006/relationships/styles" Target="styles.xml"/>`;

  const workbookRels =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Relationships xmlns="http://schemas.openxmlformats.org/package/2006/relationships">` +
    workbookRelsEntries +
    `</Relationships>`;

  const stylesXml =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<styleSheet xmlns="http://schemas.openxmlformats.org/spreadsheetml/2006/main">` +
    `<fonts count="2">` +
    `<font><sz val="11"/><color theme="1"/><name val="Calibri"/><family val="2"/></font>` +
    `<font><sz val="11"/><color rgb="FFFFFF"/><name val="Calibri"/><family val="2"/></font>` +
    `</fonts>` +
    `<fills count="3">` +
    `<fill><patternFill patternType="none"/></fill>` +
    `<fill><patternFill patternType="gray125"/></fill>` +
    `<fill><patternFill patternType="solid"><fgColor rgb="E3201B"/><bgColor indexed="64"/></patternFill></fill>` +
    `</fills>` +
    `<borders count="1"><border><left/><right/><top/><bottom/><diagonal/></border></borders>` +
    `<cellStyleXfs count="1"><xf numFmtId="0" fontId="0" fillId="0" borderId="0"/></cellStyleXfs>` +
    `<cellXfs count="4">` +
    `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"/>` +
    `<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0"/>` +
    `<xf numFmtId="0" fontId="0" fillId="0" borderId="0" xfId="0"><alignment horizontal="right"/></xf>` +
    `<xf numFmtId="0" fontId="1" fillId="2" borderId="0" xfId="0"><alignment horizontal="right"/></xf>` +
    `</cellXfs>` +
    `<cellStyles count="1"><cellStyle name="Normal" xfId="0" builtinId="0"/></cellStyles>` +
    `</styleSheet>`;

  const coreXml =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<cp:coreProperties xmlns:cp="http://schemas.openxmlformats.org/package/2006/metadata/core-properties" ` +
    `xmlns:dc="http://purl.org/dc/elements/1.1/" xmlns:dcterms="http://purl.org/dc/terms/" ` +
    `xmlns:dcmitype="http://purl.org/dc/dcmitype/" xmlns:xsi="http://www.w3.org/2001/XMLSchema-instance">` +
    `<dc:title>${escapeXml(workbookTitle || "Compliance Report")}</dc:title>` +
    `<dc:creator>InnerLogic Portal</dc:creator>` +
    `<cp:lastModifiedBy>InnerLogic Portal</cp:lastModifiedBy>` +
    `<dcterms:created xsi:type="dcterms:W3CDTF">${isoNow}</dcterms:created>` +
    `<dcterms:modified xsi:type="dcterms:W3CDTF">${isoNow}</dcterms:modified>` +
    `</cp:coreProperties>`;

  const appXml =
    `<?xml version="1.0" encoding="UTF-8" standalone="yes"?>` +
    `<Properties xmlns="http://schemas.openxmlformats.org/officeDocument/2006/extended-properties" ` +
    `xmlns:vt="http://schemas.openxmlformats.org/officeDocument/2006/docPropsVTypes">` +
    `<Application>Microsoft Excel</Application>` +
    `<DocSecurity>0</DocSecurity>` +
    `<ScaleCrop>false</ScaleCrop>` +
    `<HeadingPairs><vt:vector size="2" baseType="variant">` +
    `<vt:variant><vt:lpstr>Worksheets</vt:lpstr></vt:variant>` +
    `<vt:variant><vt:i4>${worksheetFiles.length}</vt:i4></vt:variant>` +
    `</vt:vector></HeadingPairs>` +
    `<TitlesOfParts><vt:vector size="${worksheetFiles.length}" baseType="lpstr">` +
    worksheetFiles
      .map((sheet) => `<vt:lpstr>${escapeXml(sheet.sheetName)}</vt:lpstr>`)
      .join("") +
    `</vt:vector></TitlesOfParts>` +
    `<Company></Company>` +
    `<LinksUpToDate>false</LinksUpToDate>` +
    `<SharedDoc>false</SharedDoc>` +
    `<HyperlinksChanged>false</HyperlinksChanged>` +
    `<AppVersion>16.0300</AppVersion>` +
    `</Properties>`;

  const files = [
    { name: "[Content_Types].xml", data: encodeString(contentTypes) },
    { name: "_rels/.rels", data: encodeString(rootRels) },
    { name: "docProps/core.xml", data: encodeString(coreXml) },
    { name: "docProps/app.xml", data: encodeString(appXml) },
    { name: "xl/workbook.xml", data: encodeString(workbookXml) },
    { name: "xl/_rels/workbook.xml.rels", data: encodeString(workbookRels) },
    { name: "xl/styles.xml", data: encodeString(stylesXml) },
    ...worksheetFiles.map(({ name, data }) => ({ name, data })),
  ];

  return createZip(files);
};

const GenerateCsvReportModal = ({
  open,
  onClose,
  recipients = [],
  surveyStructure = [],
  surveyTitle = "Survey Report",
  loading = false,
}) => {
  const [includeContacts, setIncludeContacts] = useState(false);
  const [selectedMetrics, setSelectedMetrics] = useState(DEFAULT_METRICS);
  const [metricsMenuOpen, setMetricsMenuOpen] = useState(false);
  const [primaryAnchorId, setPrimaryAnchorId] = useState("");
  const [secondaryAnchorIds, setSecondaryAnchorIds] = useState([]);
  const [summaryAnchorId, setSummaryAnchorId] = useState("");

  const metricsButtonRef = useRef(null);
  const breakdownButtonRef = useRef(null);
  const metricsMenuRef = useRef(null);
  const breakdownMenuRef = useRef(null);
  const [breakdownMenuOpen, setBreakdownMenuOpen] = useState(false);
  const [breakdownMenuPos, setBreakdownMenuPos] = useState({
    top: 0,
    left: 0,
    width: 0,
  });

  const dispatch = useDispatch();
  const {
    organizationId,
    employeeList,
    employeesPending,
    employeeOrgCategories,
    employeeCategoriesPending,
  } = useSelector(
    (state) => ({
      organizationId: state.auth?.organization_id,
      employeeList: state.employees?.employees || [],
      employeesPending: state.employees?.pending,
      employeeOrgCategories: state.employee_category?.employee_org_category || [],
      employeeCategoriesPending: state.employee_category?.pending,
    }),
    shallowEqual
  );

  const availableCategories = useMemo(() => {
    const rawCategories = surveyStructure?.[0]?.categories || [];
    return rawCategories
      .filter(
        (cat) =>
          cat &&
          // cat.visibility !== "private" &&
          // !cat.demographic &&
          // !cat.hidden &&
          Array.isArray(cat.options) &&
          cat.options.length > 0
      )
      .map((cat) => {
        const id = String(cat.id ?? cat.name_id ?? cat.name ?? "");
        if (!id) return null;

        const options = (cat.options || []).map((option) => ({
          id: String(option.id ?? option.value_id ?? option.value ?? option.name ?? ""),
          value: option.name ?? option.value ?? option.text ?? "",
          label: option.text || option.name || option.value || "Option",
          raw: option,
        }));

        const optionsById = new Map();
        const optionsByValue = new Map();

        options.forEach((option) => {
          if (!option.id) {
            return;
          }
          const baseEntry = {
            id: option.id,
            value: option.value,
            label: option.label,
            parentId: null,
          };
          optionsById.set(option.id, baseEntry);
          if (option.value) {
            optionsByValue.set(String(option.value).toLowerCase(), baseEntry);
          }
          if (option.label) {
            optionsByValue.set(String(option.label).toLowerCase(), baseEntry);
          }

          if (Array.isArray(option.raw?.subfactors)) {
            option.raw.subfactors.forEach((subfactor) => {
              const subId = String(
                subfactor.id ?? subfactor.value_id ?? subfactor.value ?? subfactor.name ?? ""
              );
              if (!subId) return;
              const subValue = subfactor.name ?? subfactor.value ?? subfactor.text ?? "";
              const subLabel = subfactor.text || subfactor.name || subfactor.value || "Option";
              const subEntry = {
                id: subId,
                value: subValue,
                label: subLabel,
                parentId: option.id,
              };
              optionsById.set(subId, subEntry);
              if (subValue) {
                optionsByValue.set(String(subValue).toLowerCase(), subEntry);
              }
              if (subLabel) {
                optionsByValue.set(String(subLabel).toLowerCase(), subEntry);
              }
            });
          }
        });

        return {
          id,
          name: cat.name || cat.text || "Category",
          priority: cat.priority || null,
          options: options.map((option) => ({
            id: option.id,
            value: option.value,
            label: option.label,
          })),
          optionsById,
          optionsByValue,
        };
      })
      .filter(Boolean);
  }, [surveyStructure]);

  const categoriesById = useMemo(() => {
    const map = new Map();
    availableCategories.forEach((cat) => {
      map.set(cat.id, cat);
    });
    return map;
  }, [availableCategories]);

  const categoriesByName = useMemo(() => {
    const map = new Map();
    availableCategories.forEach((cat) => {
      if (cat?.name) {
        map.set(cat.name, cat);
      }
    });
    return map;
  }, [availableCategories]);

  const getCategoryValueLabel = useCallback((categoryId, recipient) => {
    if (!recipient) return "Uncategorized";

    const key = String(categoryId);
    const categories = Array.isArray(recipient.categories) ? recipient.categories : [];

    let matchedCategory = categories.find((cat) => {
      const candidateId =
        cat?.name_id ?? cat?.category_id ?? cat?.category ?? cat?.id ?? cat?.name;
      return candidateId !== undefined && candidateId !== null && String(candidateId) === key;
    });

    const categoryDef = categoriesById.get(key);
    const categoryName = categoryDef?.name;

    if (!matchedCategory && categoryName) {
      matchedCategory = categories.find((cat) => {
        const candidateName = cat?.name ?? cat?.category;
        return (
          candidateName &&
          String(candidateName).toLowerCase() === String(categoryName).toLowerCase()
        );
      });
    }

    if (!matchedCategory) {
      return "Uncategorized";
    }

    const valueId = matchedCategory?.value_id ?? matchedCategory?.id;
    if (categoryDef?.optionsById && valueId !== undefined && valueId !== null) {
      const option = categoryDef.optionsById.get(String(valueId));
      if (option?.label) {
        return option.label;
      }
    }

    const rawValue =
      matchedCategory?.value ??
      matchedCategory?.text ??
      matchedCategory?.label ??
      matchedCategory?.name ??
      matchedCategory?.value_label ??
      "";

    if (categoryDef?.optionsByValue && rawValue) {
      const normalized = String(rawValue).toLowerCase();
      const option = categoryDef.optionsByValue.get(normalized);
      if (option?.label) {
        return option.label;
      }
    }

    return rawValue || "Uncategorized";
  }, [categoriesById, categoriesByName]);

  const secondaryAnchorOptions = useMemo(() => {
    if (!availableCategories.length) return [];
    // Show all available categories as secondary anchor options
    // Filter out the primary anchor if one is selected
    return availableCategories
      .filter((cat) => !primaryAnchorId || cat.id !== primaryAnchorId)
      .map((cat) => ({
        id: cat.id,
        label: cat.name || cat.text || "Category",
      }));
  }, [availableCategories, primaryAnchorId]);


  const primaryAnchorOptions = useMemo(() => {
    if (!availableCategories.length) return [];
    return availableCategories.map((cat) => ({
      value: cat.id,
      label: cat.name || cat.text || "Category",
    }));
  }, [availableCategories]);

  const summaryAnchorOptions = useMemo(() => {
    if (!primaryAnchorId) return [];
    const allAnchors = [primaryAnchorId, ...secondaryAnchorIds];
    return allAnchors.map((anchorId) => {
      const category = categoriesById.get(anchorId);
      return {
        id: anchorId,
        label: category?.name || "Category",
      };
    });
  }, [primaryAnchorId, secondaryAnchorIds, categoriesById]);

  const employeeCategoryMap = useMemo(() => {
    const map = new Map();
    if (!Array.isArray(employeeOrgCategories)) {
      return map;
    }

    employeeOrgCategories.forEach((entry) => {
      if (!entry) return;
      const employeeId =
        entry?.employee ?? entry?.employee_id ?? entry?.id ?? entry?.employeeId;
      if (employeeId === undefined || employeeId === null) {
        return;
      }

      const categories = Array.isArray(entry.category) ? entry.category : [];
      map.set(String(employeeId), categories);
    });

    return map;
  }, [employeeOrgCategories]);

  const getContactTags = useCallback(
    (employeeId) => {
      const categories = employeeCategoryMap.get(String(employeeId));
      if (!categories || !categories.length) {
        return {
          tags: [],
          categoryIdMap: new Map(),
          categoryNameMap: new Map(),
        };
      }

      const categoryIdMap = new Map();
      const categoryNameMap = new Map();

      const ensureSet = (map, key) => {
        if (!map.has(key)) {
          map.set(key, new Set());
        }
        return map.get(key);
      };

      const resolveCategoryDefinition = (category) => {
        const candidateKeys = [
          category?.category_id,
          category?.name_id,
          category?.id,
          category?.category,
          category?.priority,
          category?.name,
        ];

        for (const key of candidateKeys) {
          if (key === undefined || key === null || key === "") continue;
          const keyString = String(key);
          if (categoriesById.has(keyString)) {
            return categoriesById.get(keyString);
          }
          if (categoriesByName.has(keyString)) {
            return categoriesByName.get(keyString);
          }
        }

        return null;
      };

      const resolveValueLabel = (categoryDef, rawValue) => {
        if (!categoryDef || rawValue === undefined || rawValue === null || rawValue === "") {
          return rawValue ?? "";
        }

        const strValue = String(rawValue);

        if (categoryDef.optionsById?.has(strValue)) {
          return categoryDef.optionsById.get(strValue).label;
        }

        const normalized = strValue.toLowerCase();
        if (categoryDef.optionsByValue?.has(normalized)) {
          return categoryDef.optionsByValue.get(normalized).label;
        }

        return strValue;
      };

      const tags = [];

      categories.forEach((category) => {
        if (!category) return;

        const categoryDef = resolveCategoryDefinition(category);
        const categoryName =
          categoryDef?.name ||
          category?.name ||
          category?.category ||
          category?.priority;

        if (!categoryName) return;

        const idKey =
          category?.category_id ?? category?.name_id ?? category?.id ?? category?.category;
        const idSet =
          idKey !== undefined && idKey !== null
            ? ensureSet(categoryIdMap, String(idKey))
            : null;
        const nameSet = ensureSet(categoryNameMap, categoryName);

        const collectValues = [
          ...(Array.isArray(category.value_ids) ? category.value_ids : []),
          ...(category.value_id !== undefined && category.value_id !== null
            ? [category.value_id]
            : []),
          ...(Array.isArray(category.values) ? category.values : []),
          ...(category.value !== undefined && category.value !== null ? [category.value] : []),
          ...(category.text !== undefined && category.text !== null ? [category.text] : []),
        ];

        if (!collectValues.length && category.name) {
          collectValues.push(category.name);
        }

        collectValues.forEach((value) => {
          const resolved = resolveValueLabel(categoryDef, value);
          if (!resolved) return;
          nameSet.add(resolved);
          if (idSet) {
            idSet.add(resolved);
          }
        });

        tags.push({
          category: categoryName,
          values: Array.from(nameSet).sort((a, b) => String(a).localeCompare(String(b))),
          display: `${categoryName}: ${Array.from(nameSet).join(", ")}`,
        });
      });

      const categoryValues = {};
      categoryIdMap.forEach((set, id) => {
        const values = Array.from(set);
        if (values.length) {
          categoryValues[String(id)] = values[0];
        }
      });

      categoryNameMap.forEach((set, name) => {
        const values = Array.from(set);
        if (!values.length) return;
        const categoryDef = categoriesByName.get(name);
        if (categoryDef?.id) {
          const key = String(categoryDef.id);
          if (!categoryValues[key]) {
            categoryValues[key] = values[0];
          }
        }
      });

      return { tags, categoryIdMap, categoryNameMap, categoryValues };
    },
    [employeeCategoryMap, categoriesById, categoriesByName]
  );

  const getContactCategoryValueLabel = useCallback(
    (categoryId, contact) => {
      if (!contact) return "Uncategorized";

      const key = String(categoryId);

      const idValues = contact.categoryIdMap?.get(key);
      if (idValues && idValues.size) {
        return Array.from(idValues)[0];
      }

      const categoryDef = categoriesById.get(key);
      const categoryName = categoryDef?.name || key;

      const nameValues = contact.categoryNameMap?.get(categoryName);
      if (nameValues && nameValues.size) {
        return Array.from(nameValues)[0];
      }

      if (Array.isArray(contact.tags) && contact.tags.length) {
        const matchByName = contact.tags.find((tag) => tag.category === categoryName);
        if (matchByName?.values?.length) {
          return matchByName.values[0];
        }
        const fallbackTag = contact.tags[0];
        if (fallbackTag?.values?.length) {
          return fallbackTag.values[0];
        }
      }

      return "Uncategorized";
    },
    [categoriesById]
  );

  const getCategoryValueForPerson = useCallback(
    (categoryId, person) => {
      if (!person) return "Uncategorized";
      const key = String(categoryId);
      if (
        person.categoryValues &&
        Object.prototype.hasOwnProperty.call(person.categoryValues, key)
      ) {
        const value = person.categoryValues[key];
        if (value !== undefined && value !== null && value !== "") {
          return value;
        }
      }
      if (person.categoryMap && person.categoryMap.size) {
        return getCategoryValueLabel(categoryId, person);
      }
      return getContactCategoryValueLabel(categoryId, person);
    },
    [getCategoryValueLabel, getContactCategoryValueLabel]
  );

  const buildCounts = (entries = []) => {
    const counts = {
      count: entries.length,
      completed: 0,
      response_rate: 0,
    };

    entries.forEach((entry) => {
      const rawStatus = entry?.completionStatus ?? entry?.status;
      const status =
        typeof rawStatus === "string" ? rawStatus.toLowerCase() : rawStatus;

      switch (status) {
        case "complete":
        case "completed":
          counts.completed += 1;
          break;
        default:
          break;
      }
    });

    counts.response_rate =
      counts.count > 0 ? Math.round((counts.completed / counts.count) * 100) : 0;

    return counts;
  };

  const preparedContacts = useMemo(() => {
    if (!includeContacts) return [];

    const activeEmployees = (employeeList || []).filter((employee) => {
      if (!employee) return false;
      const statusCode = Number(employee.status);
      return ![98, 99, 108].includes(statusCode);
    });

    return activeEmployees.map((employee) => {
      const contactTagData = getContactTags(employee.id);
      const displayName = `${employee.first_name || ""} ${employee.last_name || ""}`.trim();
      return {
        id: employee.id,
        firstName: employee.first_name || "",
        lastName: employee.last_name || "",
        email: employee.email || "",
        tags: contactTagData.tags,
        categoryIdMap: contactTagData.categoryIdMap,
        categoryNameMap: contactTagData.categoryNameMap,
        completionStatus: "Incomplete",
        categoryValues: contactTagData.categoryValues || {},
        type: "contact",
        displayName: displayName || employee.email || "Contact",
      };
    });
  }, [includeContacts, employeeList, getContactTags]);

  const preparedRecipients = useMemo(() => {
    if (!Array.isArray(recipients)) {
      return [];
    }

    return recipients
      .filter(Boolean)
      .map((recipient, index) => {
        const firstName = recipient.first_name || recipient.firstName || "";
        const lastName = recipient.last_name || recipient.lastName || "";
        const email =
          recipient.email ||
          recipient.contact_email ||
          recipient.user_email ||
          "";

        const displayName = `${firstName || ""} ${lastName || ""}`.trim();

        const categoryValues = {};
        const categories = Array.isArray(recipient.categories) ? recipient.categories : [];

        categories.forEach((category) => {
          if (!category) return;

          const key = String(
            category?.name_id ??
              category?.category_id ??
              category?.category ??
              category?.id ??
              category?.name ??
              ""
          );

          if (!key) return;

          const categoryDef = categoriesById.get(key);

          let value =
            category?.value ??
            category?.text ??
            category?.name ??
            category?.value_label ??
            "";

          const optionId = category?.value_id ?? category?.id;
          if (categoryDef?.optionsById && optionId !== undefined && optionId !== null) {
            const option = categoryDef.optionsById.get(String(optionId));
            if (option?.label) {
              value = option.label;
            }
          }

          if (categoryDef?.optionsByValue && value) {
            const normalized = String(value).toLowerCase();
            const option = categoryDef.optionsByValue.get(normalized);
            if (option?.label) {
              value = option.label;
            }
          }

          categoryValues[key] = value || "Uncategorized";
        });

        const statusRaw =
          recipient.status_text || recipient.status || recipient.completionStatus || "";
        const statusString = String(statusRaw).toLowerCase();
        const completionStatus =
          statusString === "completed" ||
          statusString === "complete" ||
          statusRaw === 3
            ? "Complete"
            : "Incomplete";

        return {
          id: recipient.id || recipient.participantId || email || `participant-${index}`,
          firstName,
          lastName,
          email,
          displayName: displayName || email || "Participant",
          categoryValues,
          completionStatus,
          type: "participant",
        };
      });
  }, [recipients, categoriesById]);

  const contactRecords = useMemo(
    () => (includeContacts ? preparedContacts : []),
    [includeContacts, preparedContacts]
  );

  const combinedEntries = useMemo(() => {
    const dedupeMap = new Map();
    const merged = [];

    const addEntry = (entry) => {
      if (!entry) return;
      const keySource =
        entry.email || entry.id || `${entry.firstName || ""}-${entry.lastName || ""}`;
      const key = String(keySource || merged.length).toLowerCase();
      if (dedupeMap.has(key)) return;
      dedupeMap.set(key, true);
      merged.push(entry);
    };

    preparedRecipients.forEach(addEntry);

    if (includeContacts) {
      preparedContacts.forEach(addEntry);
    }

    return merged;
  }, [includeContacts, preparedRecipients, preparedContacts]);

  const contactTagCategories = useMemo(() => {
    if (!includeContacts) return [];
    const set = new Set();
    contactRecords.forEach((record) => {
      record?.tags?.forEach((tag) => {
        if (tag?.category) {
          set.add(tag.category);
        }
      });
    });
    return Array.from(set).sort((a, b) => a.localeCompare(b));
  }, [includeContacts, contactRecords]);

  const contactsLoading =
    includeContacts && (employeesPending || employeeCategoriesPending);
  const hasContactsData = includeContacts && contactRecords.length > 0;

  const exportDisabled =
    (combinedEntries.length === 0 && !hasContactsData) ||
    (includeContacts && contactsLoading);

  const contactStatusMessage = includeContacts
    ? contactsLoading
      ? "Loading organization contacts..."
      : hasContactsData
        ? `Including ${contactRecords.length} contact${contactRecords.length === 1 ? "" : "s"
        } in export`
        : "No organization contacts found"
    : "";

  const summaryRows = useMemo(() => {
    const rows = [];

    // If no primary anchor selected, show all participants in a single row
    if (!primaryAnchorId) {
      rows.push({
        key: "overview-all",
        anchorValues: { primary: "All participants" },
        counts: buildCounts(combinedEntries),
      });
      return rows;
    }

    // Build cross-tabulation: each row is a unique combination of all selected anchors
    const allAnchors = [primaryAnchorId, ...secondaryAnchorIds];
    const combinationMap = new Map();

    // Group participants by their unique combination of anchor values
    combinedEntries.forEach((entry) => {
      const values = allAnchors.map((anchorId) => 
        getCategoryValueForPerson(anchorId, entry)
      );
      const key = values.join("|||"); // Use separator to avoid collision

      if (!combinationMap.has(key)) {
        combinationMap.set(key, {
          anchorValues: values,
          entries: [],
        });
      }
      combinationMap.get(key).entries.push(entry);
    });

    // Convert map to rows array (keep entries for subtotal calculation)
    const dataRows = [];
    combinationMap.forEach(({ anchorValues, entries }, key) => {
      const anchorValueMap = {};
      allAnchors.forEach((anchorId, index) => {
        const category = categoriesById.get(anchorId);
        const categoryName = category?.name || `Anchor ${index + 1}`;
        anchorValueMap[categoryName] = anchorValues[index];
      });

      dataRows.push({
        key,
        anchorValues: anchorValueMap,
        allAnchors,
        counts: buildCounts(entries),
        entries, // Keep entries for subtotal calculation
      });
    });

    // Sort rows by anchor values for consistency
    dataRows.sort((a, b) => {
      for (let i = 0; i < allAnchors.length; i++) {
        const anchorId = allAnchors[i];
        const category = categoriesById.get(anchorId);
        const categoryName = category?.name || `Anchor ${i + 1}`;
        const valA = String(a.anchorValues[categoryName] || "");
        const valB = String(b.anchorValues[categoryName] || "");
        if (valA !== valB) {
          return valA.localeCompare(valB);
        }
      }
      return 0;
    });

    // If summary anchor is selected, group rows and add subtotals
    if (summaryAnchorId && dataRows.length > 0) {
      const summaryCategory = categoriesById.get(summaryAnchorId);
      const summaryCategoryName = summaryCategory?.name || "Summary";
      const primaryCategory = categoriesById.get(primaryAnchorId);
      const primaryCategoryName = primaryCategory?.name || "Primary";

      // Group rows by (primary value + summary value) combination
      const groups = new Map();
      dataRows.forEach((row) => {
        const primaryValue = row.anchorValues[primaryCategoryName] || "Uncategorized";
        const summaryValue = row.anchorValues[summaryCategoryName] || "Uncategorized";
        const groupKey = `${primaryValue}|||${summaryValue}`;

        if (!groups.has(groupKey)) {
          groups.set(groupKey, {
            primaryValue,
            summaryValue,
            rows: [],
          });
        }
        groups.get(groupKey).rows.push(row);
      });

      // Build final rows with subtotals for each (primary + summary) combination
      groups.forEach(({ primaryValue, summaryValue, rows: groupRows }) => {
        // Add all rows in this group
        groupRows.forEach((row) => rows.push(row));

        // Add subtotal row for this (primary + summary) combination
        const subtotalAnchorValues = {};
        allAnchors.forEach((anchorId) => {
          const category = categoriesById.get(anchorId);
          const categoryName = category?.name || "Category";

          // Show the summary value + "Total" in the summary anchor's column
          if (anchorId === summaryAnchorId) {
            subtotalAnchorValues[categoryName] = `${summaryValue} Total`;
          } else {
            subtotalAnchorValues[categoryName] = "";
          }
        });

        // Collect all entries for this group from the rows
        const allGroupEntries = [];
        groupRows.forEach((row) => {
          if (row.entries) {
            allGroupEntries.push(...row.entries);
          }
        });

        rows.push({
          key: `subtotal-${primaryValue}-${summaryValue}`,
          anchorValues: subtotalAnchorValues,
          allAnchors,
          counts: buildCounts(allGroupEntries),
          isSubtotal: true,
        });
      });
    } else {
      // No summary anchor, just add all data rows
      dataRows.forEach((row) => rows.push(row));
    }

    // Add grand total row at the end
    if (dataRows.length > 0) {
      const summaryAnchorValues = {};
      allAnchors.forEach((anchorId, index) => {
        const category = categoriesById.get(anchorId);
        const categoryName = category?.name || `Anchor ${index + 1}`;
        summaryAnchorValues[categoryName] = index === 0 ? "Total" : "";
      });

      rows.push({
        key: "summary-total",
        anchorValues: summaryAnchorValues,
        allAnchors,
        counts: buildCounts(combinedEntries),
        isSummary: true,
      });
    }

    return rows;
  }, [combinedEntries, primaryAnchorId, secondaryAnchorIds, summaryAnchorId, getCategoryValueForPerson, categoriesById]);

  const overallCounts = summaryRows[0]?.counts || buildCounts([]);

  // Show all rows, but we'll display '*' for rows with count < 5 in the table
  const summaryTableRows = useMemo(() => {
    return summaryRows;
  }, [summaryRows]);


  useEffect(() => {
    if (open && includeContacts && organizationId) {
      if (!employeeList || employeeList.length === 0) {
        dispatch({ type: LOAD_EMPLOYEES, organization_id: organizationId });
      }
      if (!employeeOrgCategories || employeeOrgCategories.length === 0) {
        dispatch({
          type: SG_GET_EMPLOYEE_ORG_CATEGORIES,
          organization_id: organizationId,
        });
      }
    }
  }, [open, includeContacts, organizationId, employeeList, employeeOrgCategories, dispatch]);

  useEffect(() => {
    if (metricsMenuOpen) {
      const close = (event) => {
        if (
          metricsMenuRef.current &&
          !metricsMenuRef.current.contains(event.target) &&
          metricsButtonRef.current &&
          !metricsButtonRef.current.contains(event.target)
        ) {
          setMetricsMenuOpen(false);
        }
      };
      window.addEventListener("mousedown", close);
      return () => window.removeEventListener("mousedown", close);
    }
  }, [metricsMenuOpen]);

  useEffect(() => {
    if (!breakdownMenuOpen) return;

    const handleClickOutside = (event) => {
      if (
        breakdownMenuRef.current &&
        breakdownMenuRef.current.contains(event.target)
      ) {
        return;
      }

      if (
        breakdownButtonRef.current &&
        breakdownButtonRef.current.contains(event.target)
      ) {
        return;
      }

      setBreakdownMenuOpen(false);
    };

    window.addEventListener("mousedown", handleClickOutside);
    return () => window.removeEventListener("mousedown", handleClickOutside);
  }, [breakdownMenuOpen]);

  useEffect(() => {
    const closeOnEscape = (event) => {
      if (event.key === "Escape") {
        if (metricsMenuOpen) {
          setMetricsMenuOpen(false);
          event.stopPropagation();
          return;
        }
        if (breakdownMenuOpen) {
          setBreakdownMenuOpen(false);
          event.stopPropagation();
          return;
        }
        onClose?.();
      }
    };

    if (open) {
      window.addEventListener("keydown", closeOnEscape);
      return () => window.removeEventListener("keydown", closeOnEscape);
    }
  }, [open, metricsMenuOpen, breakdownMenuOpen, onClose]);

  const addMetric = (metricId) => {
    if (!METRIC_DEFINITIONS[metricId]) return;
    setSelectedMetrics((prev) => {
      if (prev.includes(metricId)) return prev;
      return [...prev, metricId];
    });
  };

  const removeMetric = (metricId) => {
    if (DEFAULT_METRICS.includes(metricId)) return;
    setSelectedMetrics((prev) => prev.filter((id) => id !== metricId));
  };

  const handlePrimaryChange = (event) => {
    const value = event.target.value;
    setPrimaryAnchorId(value);
    // Clear secondary anchors when primary anchor changes
    setSecondaryAnchorIds([]);
  };

  const handleSecondaryAnchorToggle = (categoryId) => {
    setSecondaryAnchorIds((prev) => {
      if (prev.includes(categoryId)) {
        return prev.filter((id) => id !== categoryId);
      }
      return [...prev, categoryId];
    });
  };

  const availableMetricsToAdd = useMemo(() => {
    return Object.values(METRIC_DEFINITIONS).filter(
      (metric) => !selectedMetrics.includes(metric.id)
    );
  }, [selectedMetrics]);


  const formatMetric = (metricId, counts, isSubtotalOrSummary = false) => {
    const rawValue = counts?.[metricId];
    const value = rawValue ?? 0;
    const count = counts?.count ?? 0;

    // Show '*' for completed and response_rate when count < 5 (applies to all rows including totals)
    if (count < 5 && (metricId === "completed" || metricId === "response_rate")) {
      return "*";
    }

    if (metricId === "response_rate") {
      return `${value}%`;
    }
    return value;
  };

  const getMetricValue = (metricId, counts, isSubtotalOrSummary = false) => {
    const value = counts?.[metricId];
    const count = counts?.count ?? 0;

    // Show '*' for completed and response_rate when count < 5 (applies to all rows including totals)
    if (count < 5 && (metricId === "completed" || metricId === "response_rate")) {
      return "*";
    }

    if (value === null || value === undefined) return "";

    // Add % to response_rate values (when not *)
    if (metricId === "response_rate" && typeof value === "number") {
      return `${value}%`;
    }

    if (typeof value === "number") return value;
    const coerced = Number(value);
    return Number.isFinite(coerced) ? coerced : String(value);
  };

  const sanitizeFilename = (name) => {
    return name.replace(/[<>:"/\\|?*]+/g, "").replace(/\s+/g, "_").slice(0, 80);
  };

  const handleExportWorkbook = () => {
    const sheetDefinitions = [];

    // Build headers: anchor columns + metric columns
    const allAnchors = primaryAnchorId ? [primaryAnchorId, ...secondaryAnchorIds] : [];
    const anchorHeaders = allAnchors.map((anchorId) => {
      const category = categoriesById.get(anchorId);
      return category?.name || "Category";
    });

    const summaryHeaders = [
      ...anchorHeaders,
      ...selectedMetrics.map((id) => METRIC_DEFINITIONS[id].label),
    ];

    // Build data rows (include all rows, with '*' shown for rows with count < 4)
    const summaryDataRows = summaryTableRows.map((row) => {
      const anchorValues = allAnchors.map((anchorId) => {
        const category = categoriesById.get(anchorId);
        const categoryName = category?.name || "Category";
        return row.anchorValues[categoryName] || "";
      });

      return [
        ...anchorValues,
        ...selectedMetrics.map((metricId) => getMetricValue(metricId, row.counts, row.isSubtotal || row.isSummary)),
      ];
    });

    // Build row metadata to identify Total rows
    const summaryRowMetadata = summaryTableRows.map((row) => {
      // Check if this is a "Total" row by checking anchor values
      const isTotalRow = Object.values(row.anchorValues || {}).some(
        (value) => String(value).toLowerCase().includes("total")
      );
      return { isTotalRow };
    });

    sheetDefinitions.push({
      name: "Compliance Report",
      heading: "Compliance Summary",
      headers: summaryHeaders,
      rows: summaryDataRows,
      rowMetadata: summaryRowMetadata,
    });

    const workbookData = buildWorkbookZip({
      sheets: sheetDefinitions,
      workbookTitle: surveyTitle,
    });

    const blob = new Blob([workbookData], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement("a");
    link.href = url;
    link.download = `${sanitizeFilename(surveyTitle)}_compliance.xlsx`;
    link.click();
    window.URL.revokeObjectURL(url);
  };

  const metricChips = selectedMetrics.map((metricId) => {
    const definition = METRIC_DEFINITIONS[metricId];
    if (!definition) return null;
    const allowRemove = !DEFAULT_METRICS.includes(metricId);
    return (
      <MetricChip key={metricId}>
        {definition.label}
        {allowRemove && (
          <RemoveMetricButton onClick={() => removeMetric(metricId)} aria-label={`Remove ${definition.label}`}>
            ×
          </RemoveMetricButton>
        )}
      </MetricChip>
    );
  });

  if (!open) return null;

  return createPortal(
    <Overlay onClick={onClose}>
      <Modal onClick={(event) => event.stopPropagation()}>
        <Header>
          <Title>Generate CSV Report</Title>
          <HeaderActions>
            <ExportButton
              type="button"
              onClick={handleExportWorkbook}
              disabled={exportDisabled}
            >
              Export XLSX
            </ExportButton>
            <CloseButton type="button" onClick={onClose} aria-label="Close">
              ×
            </CloseButton>
          </HeaderActions>
        </Header>

        <Content>
          {loading && (
            <LoadingOverlay>
              <LoadingSpinner />
              <LoadingText>Loading data...</LoadingText>
            </LoadingOverlay>
          )}
          <ControlGrid style={{ opacity: loading ? 0.3 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
            <ControlColumn>
              <ControlLabel>Primary anchor</ControlLabel>
              <Select
                value={primaryAnchorId}
                onChange={handlePrimaryChange}
                disabled={!primaryAnchorOptions.length}
              >
                <option value="">Select primary anchor...</option>
                {primaryAnchorOptions.length ? (
                  primaryAnchorOptions.map((option) => (
                    <option key={option.value} value={option.value}>
                      {option.label}
                    </option>
                  ))
                ) : (
                    <option value="" disabled>No categories available</option>
                )}
              </Select>
            </ControlColumn>

            <ControlColumn>
              <ControlLabel>Secondary anchors (optional)</ControlLabel>
              <MultiSelectContainer>
                <MultiSelectButton
                  ref={breakdownButtonRef}
                  type="button"
                  onClick={() => {
                    if (breakdownMenuOpen) {
                      setBreakdownMenuOpen(false);
                      return;
                    }
                    if (breakdownButtonRef.current) {
                      const rect = breakdownButtonRef.current.getBoundingClientRect();
                      setBreakdownMenuPos({
                        top: rect.bottom + 4,
                        left: rect.left,
                        width: rect.width,
                      });
                    }
                    setBreakdownMenuOpen(true);
                  }}
                  hasSelection={secondaryAnchorIds.length > 0}
                  disabled={!secondaryAnchorOptions.length}
                >
                  {secondaryAnchorIds.length
                    ? `${secondaryAnchorIds.length} selected`
                    : "Add secondary anchors"}
                  <DropdownCaret />
                </MultiSelectButton>
                {breakdownMenuOpen &&
                  createPortal(
                    <MultiSelectDropdown
                      ref={breakdownMenuRef}
                      style={{
                        top: breakdownMenuPos.top,
                        left: breakdownMenuPos.left,
                        minWidth: breakdownMenuPos.width,
                      }}
                    >
                      {secondaryAnchorOptions.map((option) => {
                        const checked = secondaryAnchorIds.includes(option.id);
                          return (
                            <MultiSelectOption
                              key={option.id}
                              type="button"
                              onClick={() => handleSecondaryAnchorToggle(option.id)}
                            >
                              <CheckboxSquare checked={checked}>
                                {checked && <CheckIcon>✓</CheckIcon>}
                              </CheckboxSquare>
                              <span>{option.label}</span>
                            </MultiSelectOption>
                          );
                        })}
                      {!secondaryAnchorOptions.length && (
                        <MultiSelectEmpty>
                          {primaryAnchorId ? "No additional categories available" : "Select a primary anchor first"}
                        </MultiSelectEmpty>
                      )}
                    </MultiSelectDropdown>,
                    document.body
                  )}
              </MultiSelectContainer>
              {secondaryAnchorIds.length === 0 && primaryAnchorId && (
                <BreakdownHint>
                  Add secondary anchors to create more granular breakdowns.
                </BreakdownHint>
              )}
              {!primaryAnchorId && (
                <BreakdownHint>
                  Select a primary anchor first.
                </BreakdownHint>
              )}
            </ControlColumn>

            <ControlColumn>
              <ControlLabel>Summary anchor (optional)</ControlLabel>
              <Select
                value={summaryAnchorId}
                onChange={(e) => setSummaryAnchorId(e.target.value)}
                disabled={!summaryAnchorOptions.length}
              >
                <option value="">No grouping</option>
                {summaryAnchorOptions.map((option) => (
                  <option key={option.id} value={option.id}>
                    {option.label}
                  </option>
                ))}
              </Select>
              <BreakdownHint>
                Group rows and show subtotals for each group.
              </BreakdownHint>
            </ControlColumn>

            <ControlColumn>
              <ControlLabel>Include organization contacts</ControlLabel>
              <ToggleSwitch
                type="button"
                checked={includeContacts}
                onClick={() => setIncludeContacts((prev) => !prev)}
                aria-pressed={includeContacts}
              >
                <ToggleTrack checked={includeContacts}>
                  <ToggleThumb checked={includeContacts} />
                </ToggleTrack>
                <ToggleText>{includeContacts ? "On" : "Off"}</ToggleText>
              </ToggleSwitch>
              {includeContacts && (
                <ContactStatus>{contactStatusMessage}</ContactStatus>
              )}
            </ControlColumn>
          </ControlGrid>

          <MetricsRow style={{ opacity: loading ? 0.3 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
            <MetricsLabel>Columns</MetricsLabel>
            <MetricChipList>{metricChips}</MetricChipList>
            <AddMetricWrapper>
              <AddMetricButton
                ref={metricsButtonRef}
                type="button"
                onClick={() => setMetricsMenuOpen((prev) => !prev)}
                disabled={!availableMetricsToAdd.length}
                aria-haspopup="menu"
                aria-expanded={metricsMenuOpen}
              >
                +
              </AddMetricButton>
              {metricsMenuOpen && (
                <MetricsMenu ref={metricsMenuRef}>
                  {availableMetricsToAdd.map((metric) => (
                    <MetricsMenuItem
                      key={metric.id}
                      type="button"
                      onClick={() => {
                        addMetric(metric.id);
                        setMetricsMenuOpen(false);
                      }}
                    >
                      {metric.label}
                    </MetricsMenuItem>
                  ))}
                  {!availableMetricsToAdd.length && (
                    <MetricsMenuEmpty>No additional fields</MetricsMenuEmpty>
                  )}
                </MetricsMenu>
              )}
            </AddMetricWrapper>
          </MetricsRow>

          <ComplianceSummary style={{ opacity: loading ? 0.3 : 1, pointerEvents: loading ? 'none' : 'auto' }}>
            {overallCounts.completed}/{overallCounts.count} ({overallCounts.response_rate}%)
            completed the survey
          </ComplianceSummary>

          {summaryTableRows.length ? (
            <Table style={{ opacity: loading ? 0.3 : 1 }}>
              <thead>
                <tr>
                  {(() => {
                    const allAnchors = primaryAnchorId ? [primaryAnchorId, ...secondaryAnchorIds] : [];
                    const anchorHeaders = allAnchors.map((anchorId) => {
                      const category = categoriesById.get(anchorId);
                      return category?.name || "Category";
                    });

                    return (
                      <>
                        {anchorHeaders.length > 0 ? (
                          anchorHeaders.map((header, index) => (
                            <TableHeaderCell key={`anchor-${index}`}>
                              {header}
                            </TableHeaderCell>
                          ))
                        ) : (
                            <TableHeaderCell>Group</TableHeaderCell>
                        )}
                        {selectedMetrics.map((metricId) => (
                          <TableHeaderCell key={metricId}>
                            {METRIC_DEFINITIONS[metricId]?.label || metricId}
                          </TableHeaderCell>
                        ))}
                      </>
                    );
                  })()}
                </tr>
              </thead>
              <tbody>
                {summaryTableRows.map((row) => {
                  const allAnchors = primaryAnchorId ? [primaryAnchorId, ...secondaryAnchorIds] : [];

                  // Check if this is a "Total" row by checking anchor values
                  const isTotalRow = Object.values(row.anchorValues || {}).some(
                    (value) => String(value).toLowerCase().includes("total")
                  );

                  // Styling for summary and subtotal rows
                  const rowStyle = {};
                  if (row.isSummary) {
                    rowStyle.fontWeight = 600;
                    rowStyle.borderTop = "2px solid #d1d5db";
                    rowStyle.backgroundColor = "#f9fafb";
                  } else if (row.isSubtotal) {
                    rowStyle.fontWeight = 600;
                    rowStyle.borderTop = "1px solid #e5e7eb";
                    rowStyle.backgroundColor = "#fefefe";
                  }

                  // Cell style for Total rows - apply red background with white text
                  const cellStyle = isTotalRow
                    ? { fontWeight: 600, color: "#FFFFFF", backgroundColor: "#E3201B" }
                    : row.isSubtotal || row.isSummary
                      ? { fontWeight: 600 }
                      : {};

                  return (
                    <tr key={row.key} style={rowStyle}>
                      {allAnchors.length > 0 ? (
                        allAnchors.map((anchorId, index) => {
                          const category = categoriesById.get(anchorId);
                          const categoryName = category?.name || `Anchor ${index + 1}`;
                          return (
                            <TableCell key={`anchor-${index}`} style={cellStyle}>
                              {row.anchorValues[categoryName] || ""}
                            </TableCell>
                          );
                        })
                      ) : (
                          <TableCell style={cellStyle}>
                          {row.anchorValues.primary || "All participants"}
                        </TableCell>
                      )}
                      {selectedMetrics.map((metricId) => (
                        <TableCell key={metricId} style={cellStyle}>
                          {formatMetric(metricId, row.counts, row.isSubtotal || row.isSummary)}
                        </TableCell>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </Table>
          ) : (
            <EmptyState>
              <EmptyStateIcon>📊</EmptyStateIcon>
                <EmptyStateTitle>No compliance data available</EmptyStateTitle>
              <EmptyStateText>
                  {primaryAnchorId
                    ? "No participants found with the selected anchors."
                    : "Select a primary anchor to view compliance data."}
              </EmptyStateText>
            </EmptyState>
          )}
        </Content>
      </Modal>
    </Overlay>,
    document.body
  );
};

const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.55);
  z-index: 1200;
  display: flex;
  align-items: center;
  justify-content: center;
  backdrop-filter: blur(1px);
  padding: 32px;
`;

const Modal = styled.div`
  width: 100%;
  max-width: 960px;
  background: #ffffff;
  border-radius: 16px;
  box-shadow: 0 24px 48px rgba(13, 25, 62, 0.18);
  display: flex;
  flex-direction: column;
  max-height: 90vh;
  overflow: hidden;
`;

const Header = styled.div`
  padding: 20px 28px;
  border-bottom: 1px solid #e3e6ef;
  display: flex;
  align-items: center;
  justify-content: space-between;
  background: #f7f9ff;
`;

const HeaderActions = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
`;

const Title = styled.h2`
  margin: 0;
  font-family: "Poppins";
  font-size: 20px;
  font-weight: 600;
  color: #27304a;
`;

const CloseButton = styled.button`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  border: none;
  background: transparent;
  color: #6b7280;
  font-size: 24px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s ease, color 0.2s ease;

  &:hover {
    background: #e5e7eb;
    color: #111827;
  }
`;

const ExportButton = styled.button`
  padding: 10px 18px;
  border-radius: 8px;
  border: 1px solid #476dfa;
  background: #476dfa;
  color: #ffffff;
  font-family: "Poppins";
  font-size: 14px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease, box-shadow 0.2s ease;

  &:hover:not(:disabled) {
    background: #395be8;
    box-shadow: 0 8px 16px rgba(71, 109, 250, 0.28);
  }

  &:disabled {
    background: #c7d2fe;
    border-color: #c7d2fe;
    cursor: not-allowed;
  }
`;

const Content = styled.div`
  padding: 24px 28px 32px;
  overflow-y: auto;
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 24px;
  position: relative;
`;

const ControlGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(220px, 1fr));
  gap: 20px;
`;

const ControlColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const ControlLabel = styled.label`
  font-family: "Poppins";
  font-size: 13px;
  font-weight: 500;
  color: #4b5563;
`;

const Select = styled.select`
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid #d1d5db;
  font-family: "Poppins";
  font-size: 13px;
  color: #111827;
  background: #ffffff;
  appearance: none;
  background-image: url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' width='18' height='18'%3e%3cpolygon points='4,6 14,6 9,12' fill='%236b7280'/%3e%3c/svg%3e");
  background-repeat: no-repeat;
  background-position: right 12px center;
  background-size: 10px;

  &:focus {
    outline: none;
    border-color: #476dfa;
    box-shadow: 0 0 0 3px rgba(71, 109, 250, 0.15);
  }
`;

const MultiSelectContainer = styled.div`
  position: relative;
`;

const MultiSelectButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid ${(props) => (props.hasSelection ? "#476dfa" : "#d1d5db")};
  background: #ffffff;
  color: ${(props) => (props.hasSelection ? "#111827" : "#6b7280")};
  font-family: "Poppins";
  font-size: 13px;
  cursor: pointer;
  transition: border 0.2s ease, color 0.2s ease;

  &:hover {
    border-color: #476dfa;
    color: #111827;
  }
`;

const DropdownCaret = styled.span`
  display: inline-flex;
  width: 0;
  height: 0;
  margin-left: 8px;
  border-left: 5px solid transparent;
  border-right: 5px solid transparent;
  border-top: 6px solid #6b7280;
`;

const MetricsRow = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  flex-wrap: wrap;
`;

const MetricsLabel = styled.span`
  font-family: "Poppins";
  font-size: 13px;
  font-weight: 500;
  color: #4b5563;
`;

const MetricChipList = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
  flex: 1;
`;

const MetricChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 10px;
  background: #eef2ff;
  color: #27304a;
  border-radius: 999px;
  font-family: "Poppins";
  font-size: 12px;
`;

const RemoveMetricButton = styled.button`
  background: none;
  border: none;
  color: #4b5563;
  cursor: pointer;
  font-size: 14px;
  padding: 0 2px;

  &:hover {
    color: #111827;
  }
`;

const AddMetricWrapper = styled.div`
  position: relative;
`;

const AddMetricButton = styled.button`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  border: 1px solid #d1d5db;
  background: #ffffff;
  color: #4b5563;
  font-size: 18px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  transition: all 0.2s ease;

  &:hover:not(:disabled) {
    background: #f3f4f6;
    color: #111827;
  }

  &:disabled {
    color: #cbd5f5;
    border-color: #e5e7eb;
    cursor: not-allowed;
  }
`;

const MetricsMenu = styled.div`
  position: absolute;
  top: 40px;
  right: 0;
  width: 200px;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 12px 24px rgba(15, 23, 42, 0.12);
  border: 1px solid #e5e7eb;
  padding: 8px 0;
  z-index: 10;
`;

const MetricsMenuItem = styled.button`
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: none;
  text-align: left;
  font-family: "Poppins";
  font-size: 13px;
  color: #1f2937;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }
`;

const MetricsMenuEmpty = styled.div`
  padding: 10px 16px;
  font-family: "Poppins";
  font-size: 12px;
  color: #6b7280;
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 8px;
  flex-wrap: wrap;
`;

const ComplianceSummary = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  font-family: "Poppins";
  font-size: 13px;
  color: #4b5563;
  margin: 8px 0 12px;
`;

const TabButton = styled.button`
  padding: 8px 16px;
  border-radius: 999px;
  border: none;
  background: ${(props) => (props.active ? "#476dfa" : "#eef2ff")};
  color: ${(props) => (props.active ? "#ffffff" : "#27304a")};
  font-family: "Poppins";
  font-size: 13px;
  font-weight: 500;
  cursor: pointer;
  transition: background 0.2s ease, color 0.2s ease;
`;

const Table = styled.table`
  width: 100%;
  border-collapse: collapse;
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid #e5e7eb;
`;

const TableHeaderCell = styled.th`
  text-align: left;
  padding: 12px 16px;
  background: #f3f4f6;
  font-family: "Poppins";
  font-size: 12px;
  font-weight: 600;
  color: #4b5563;
  border-bottom: 1px solid #e5e7eb;
`;

const TableCell = styled.td`
  padding: 12px 16px;
  font-family: "Poppins";
  font-size: 13px;
  color: #1f2937;
  border-bottom: 1px solid #edf2f7;
`;

const EmptyState = styled.div`
  margin-top: 32px;
  padding: 32px;
  background: #f9fafb;
  border-radius: 12px;
  text-align: center;
  border: 1px dashed #d1d5db;
`;

const EmptyStateIcon = styled.div`
  font-size: 24px;
  margin-bottom: 8px;
`;

const EmptyStateTitle = styled.h3`
  margin: 0;
  font-family: "Poppins";
  font-size: 16px;
  font-weight: 600;
  color: #111827;
`;

const EmptyStateText = styled.p`
  margin: 6px 0 0;
  font-family: "Poppins";
  font-size: 13px;
  color: #6b7280;
`;

const ToggleSwitch = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 10px;
  border: none;
  background: none;
  cursor: pointer;
  font-family: "Poppins";
  font-size: 13px;
  color: #1f2937;
`;

const ToggleTrack = styled.span`
  width: 44px;
  height: 24px;
  background: ${(props) => (props.checked ? "#476dfa" : "#d1d5db")};
  border-radius: 999px;
  padding: 3px;
  display: inline-flex;
  transition: background 0.2s ease;
`;

const ToggleThumb = styled.span`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  background: #ffffff;
  transform: translateX(${(props) => (props.checked ? "20px" : "0")});
  transition: transform 0.2s ease;
`;

const ToggleText = styled.span`
  min-width: 24px;
`;

const ContactStatus = styled.div`
  margin-top: 6px;
  font-family: "Poppins";
  font-size: 12px;
  color: #6b7280;
`;

const BreakdownHint = styled.div`
  margin-top: 6px;
  font-family: "Poppins";
  font-size: 12px;
  color: #6b7280;
`;

const MultiSelectDropdown = styled.div`
  position: fixed;
  background: #ffffff;
  border-radius: 10px;
  box-shadow: 0 16px 32px rgba(15, 23, 42, 0.18);
  border: 1px solid #e5e7eb;
  padding: 8px 0;
  z-index: 1300;
`;

const MultiSelectOption = styled.button`
  width: 100%;
  padding: 10px 16px;
  background: transparent;
  border: none;
  text-align: left;
  font-family: "Poppins";
  font-size: 13px;
  color: #1f2937;
  display: flex;
  align-items: center;
  gap: 10px;
  cursor: pointer;

  &:hover {
    background: #f3f4f6;
  }
`;

const CheckboxSquare = styled.span`
  width: 16px;
  height: 16px;
  border-radius: 4px;
  border: 1px solid ${(props) => (props.checked ? "#476dfa" : "#d1d5db")};
  background: ${(props) => (props.checked ? "#476dfa" : "#ffffff")};
  display: inline-flex;
  align-items: center;
  justify-content: center;
  color: #ffffff;
  font-size: 12px;
  transition: all 0.15s ease;
`;

const CheckIcon = styled.span`
  line-height: 1;
`;

const MultiSelectEmpty = styled.div`
  padding: 10px 16px;
  font-family: "Poppins";
  font-size: 12px;
  color: #6b7280;
`;

const LoadingOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(255, 255, 255, 0.9);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 10;
  border-radius: 12px;
`;

const LoadingSpinner = styled.div`
  width: 40px;
  height: 40px;
  border: 4px solid #f0f0f0;
  border-top: 4px solid #476dfa;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin-bottom: 12px;

  @keyframes spin {
    0% {
      transform: rotate(0deg);
    }
    100% {
      transform: rotate(360deg);
    }
  }
`;

const LoadingText = styled.div`
  font-family: "Poppins";
  font-weight: 500;
  font-size: 14px;
  color: #414141;
`;

export default GenerateCsvReportModal;
